import { Injectable } from '@angular/core';
import {
  BrandColorLabels,
  Campaign,
  ColorSwatch,
  ColorSwatches,
  CustomColorSwatchesLimit,
  ColorSwatchesStoragePrefix,
  Style,
} from '../_models';
import { CampaignService } from './campaign.service';
import { StorageService } from './storage.service';
import { StyleService } from './style.service';

// Swatches shared by every color picker, kept in localStorage per brand: its Brand > Branding colors
// (refreshed in the background whenever the brand is fetched) and the custom colors the user saved.
@Injectable()
export class ColorSwatchService {
  brandColors: ColorSwatch[] = [];
  customColors: string[] = [];

  constructor(
    private campaignService: CampaignService,
    private storageService: StorageService,
    private styleService: StyleService
  ) {}

  loadBrandColors(campaign: Campaign) {
    if (campaign && campaign.styles && campaign.styles.length) {
      this.styleService.get(campaign.styles[0]).subscribe((style: Style) => {
        this.setBrandColors(campaign.id, style.variables);
      });
    }
  }

  setBrandColors(campaignId: string | number, variables: { [key: string]: string }) {
    const swatches = this.read(campaignId);
    swatches.brand = [];
    Object.keys(BrandColorLabels).forEach((key) => {
      if (variables && variables[key]) {
        swatches.brand.push({ label: BrandColorLabels[key], color: variables[key] });
      }
    });
    this.write(campaignId, swatches);
  }

  // Reloads the current brand's swatches; pickers call this when they open.
  refresh() {
    const swatches = this.read(this.campaignService.getCurrentCampaignId());
    this.brandColors = swatches.brand;
    this.customColors = swatches.custom;
  }

  addCustomColor(color: string) {
    if (color && !this.customColors.includes(color)) {
      this.saveCustomColors([...this.customColors, color].slice(-CustomColorSwatchesLimit));
    }
  }

  removeCustomColor(color: string) {
    this.saveCustomColors(this.customColors.filter((item) => item !== color));
  }

  private saveCustomColors(colors: string[]) {
    const campaignId = this.campaignService.getCurrentCampaignId();
    const swatches = this.read(campaignId);
    swatches.custom = colors;
    this.customColors = colors;
    this.write(campaignId, swatches);
  }

  private read(campaignId: string | number): ColorSwatches {
    return { brand: [], custom: [], ...this.storageService.get(ColorSwatchesStoragePrefix + campaignId) };
  }

  // Stored via StorageService (6-hour expiry); brand colors are re-fetched on every app load.
  private write(campaignId: string | number, swatches: ColorSwatches) {
    if (campaignId) {
      this.storageService.set(ColorSwatchesStoragePrefix + campaignId, swatches);
    }
  }
}
